import { useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { Entitlement, GRANTABLE_LICENSE_TYPES, LicenseType, licenseTypeLabels, ReconcileReport, UserSearchResult } from "../api/models"
import UserFilter from "./widgets/UserFilter"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// Grant a user the sku's latest RELEASED version at a chosen license type:
// the backend records an entitlement (origin=grant) and deposits that
// version's claim licenses in the same call, so the dialog ends on a "done"
// step showing the deposit report (AddVersionDialog pattern). Revocation goes
// through the Users list's existing revoke flow. Conditionally mounted.
export function GrantSkuDialog({ skuId, latestVersion, ilokAvailable, accessToken, onClose, onGranted }: {
  skuId: string
  // the latest RELEASED version's "MAJOR.MINOR.PATCH" (display only); null
  // while versions are still loading or nothing is released (the server 400s)
  latestVersion: string | null
  // the owner org has iLok switched on — offers the "allow iLok" toggle
  ilokAvailable: boolean
  accessToken: string
  onClose: () => void
  onGranted: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [user, setUser] = useState<UserSearchResult | null>(null)
  const [licenseType, setLicenseType] = useState(LicenseType.Standard)
  // whether the grantee may request an iLok counterpart (orgs with iLok on)
  const [ilokEnabled, setIlokEnabled] = useState(true)
  // the grant's lifetime in days; empty = perpetual
  const [licenseExpiryText, setLicenseExpiryText] = useState("")
  const [step, setStep] = useState<"form" | "saving" | "done" | "error">("form")
  const [errorMessage, setErrorMessage] = useState("")
  const [report, setReport] = useState<ReconcileReport | null>(null)
  const [granted, setGranted] = useState<Entitlement | null>(null)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const busy = step === "saving"
  const licenseExpiryNum = licenseExpiryText === "" ? null : Number(licenseExpiryText)
  const licenseExpiryValid = licenseExpiryNum === null || (Number.isInteger(licenseExpiryNum) && licenseExpiryNum >= 1)
  // an expiring entitlement never gets an iLok deposit (server rule) — the
  // toggle is moot then
  const ilokApplies = licenseExpiryNum === null
  const formValid = user !== null && licenseExpiryValid

  const handleSubmit = async () => {
    if (!formValid) return
    setStep("saving")
    setErrorMessage("")
    try {
      const { entitlement, reconcile } = await Api.grantSku({
        skuId,
        userId: user!.id,
        licenseType,
        ilokEnabled: ilokAvailable && ilokApplies ? ilokEnabled : undefined,
        licenseExpiryDays: licenseExpiryNum ?? undefined,
        accessToken,
      })
      setGranted(entitlement)
      setReport(reconcile)
      onGranted()
      setStep("done")
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog redeem-dialog-overflow"
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Grant SKU</h2>

        {step === "done" && report ? (
          <>
            <p>
              Granted. Deposited {report.depositedCount} license{report.depositedCount === 1 ? "" : "s"}.
              {granted?.expiresAt && <> Valid until {new Date(granted.expiresAt).toLocaleDateString()}.</>}
            </p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Close</button>
            </div>
          </>
        ) : (
          <>
            <p className="generate-codes-subtitle">
              Grants the latest released version{latestVersion !== null ? ` (v${latestVersion})` : ""} —
              the user gets its licenses deposited immediately; revoke the
              grant from the Users list to take them back.
            </p>

            <div className="generate-codes-form">
              <div className="generate-codes-field">
                <span className="generate-codes-label">User</span>
                <UserFilter
                  selected={user}
                  onSelect={setUser}
                  accessToken={accessToken}
                />
              </div>

              <div className="generate-codes-field">
                <span className="generate-codes-label">License type</span>
                <select
                  value={licenseType}
                  onChange={(e) => setLicenseType(e.target.value as LicenseType)}
                  className="generate-codes-input"
                  disabled={busy}
                  style={{ width: "auto" }}
                >
                  {GRANTABLE_LICENSE_TYPES.map((t) => (
                    <option key={t} value={t}>{licenseTypeLabels[t]}</option>
                  ))}
                </select>
              </div>

              <label className="generate-codes-field">
                <span className="generate-codes-label">License expiry (days)</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={licenseExpiryText}
                  onChange={(e) => setLicenseExpiryText(e.target.value)}
                  className="generate-codes-input"
                  disabled={busy}
                  placeholder="never"
                />
                <span className="generate-codes-hint">Duration of the grant from today — leave empty for a permanent license</span>
              </label>

              {ilokAvailable && (
                <label className="generate-codes-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={ilokApplies && ilokEnabled}
                    onChange={(e) => setIlokEnabled(e.target.checked)}
                    disabled={busy || !ilokApplies}
                  />
                  <span className="generate-codes-label" style={{ margin: 0 }}>
                    Allow an iLok license
                  </span>
                  <span className="generate-codes-hint" style={{ margin: 0 }}>
                    {ilokApplies
                      ? "untick for a native-only grant — the user can't request an iLok counterpart"
                      : "not available for expiring grants — iLok licenses are perpetual"}
                  </span>
                </label>
              )}
            </div>

            {step === "error" && errorMessage && (
              <p className="generate-codes-error">{errorMessage}</p>
            )}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
              <button
                className="btn-upgrade"
                disabled={!formValid || busy}
                onClick={handleSubmit}
              >
                {busy ? <><Spinner size={12} /> Granting…</> : "Grant"}
              </button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}
